import { LoadSource, OcrFormat, type OcrParseResult } from "@haulsheet/core";
import type { BoardRow } from "../components/check/BoardPicker";
import { EMPTY_LOAD_FORM, type LoadFormValues } from "../components/check/LoadForm";

/** One parsed load from core OCR (all string fields; see OcrLoadFields). */
export type OcrLoad = Record<string, unknown>;

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "";
}

/** "$1,700.00" → "1700"; "" when not a number. */
export function money(v: unknown): string {
  const s = str(v).replace(/[$,\s]/g, "");
  if (!s) return "";
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : "";
}

function wholeNumber(v: unknown): string {
  const s = str(v).replace(/[,\s]/g, "").replace(/mi(les)?$/i, "");
  if (!s) return "";
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.round(n)) : "";
}

/** Loads the parser found (fields.loads, else the single fields.load). */
export function ocrLoads(parsed: OcrParseResult): OcrLoad[] {
  const loads = parsed.fields["loads"];
  if (Array.isArray(loads)) {
    return loads.filter((l): l is OcrLoad => !!l && typeof l === "object");
  }
  const one = parsed.fields["load"];
  return one && typeof one === "object" ? [one as OcrLoad] : [];
}

/** Board refs are "Truckstop, <company>": a source label, not a load number. */
function splitRef(ref: string): { refNumber: string; brokerName: string } {
  const m = ref.match(/^Truckstop(?:,\s*(.*))?$/i);
  if (m) return { refNumber: "", brokerName: (m[1] ?? "").trim() };
  return { refNumber: ref, brokerName: "" };
}

/** Map a parsed load onto the Check form. Unknown/blank fields stay blank. */
export function ocrLoadToForm(load: OcrLoad): LoadFormValues {
  const { refNumber, brokerName } = splitRef(str(load.ref));
  const extras = ["tolls", "tarpFee", "permitCost", "other"]
    .map((k) => Number(money(load[k]) || 0))
    .reduce((a, b) => a + b, 0);
  const warnings = Array.isArray(load.unreadable)
    ? (load.unreadable as unknown[]).map(str).filter(Boolean)
    : [];
  const noteParts = [str(load.notes), ...warnings.map((w) => `Check: ${w}`)].filter(Boolean);
  return {
    ...EMPTY_LOAD_FORM,
    originText: str(load.origin),
    destinationText: str(load.destination),
    linehaulDollars: money(load.linehaul),
    fscPerMileDollars: money(load.fsc),
    accessorialsDollars: extras > 0 ? String(Math.round(extras * 100) / 100) : "",
    loadedMiles: wholeNumber(load.miles),
    deadheadMiles: wholeNumber(load.deadhead),
    weight: str(load.weight),
    width: str(load.width),
    height: str(load.height),
    length: str(load.length),
    note: noteParts.join("\n"),
    brokerName,
    refNumber,
  };
}

/** Picker row for each parsed load, carrying the full form values. */
export function ocrLoadsToBoardRows(loads: OcrLoad[]): BoardRow[] {
  return loads.map((load, i) => {
    const form = ocrLoadToForm(load);
    const detail = [str(load.equipment), str(load.pickup) && `pick up ${str(load.pickup)}`, form.brokerName]
      .filter(Boolean)
      .join(" · ");
    return {
      id: `ocr-${i}`,
      origin: form.originText || "Origin not read",
      destination: form.destinationText || "Destination not read",
      linehaulDollars: Number(form.linehaulDollars || 0),
      miles: Number(form.loadedMiles || 0),
      detail,
      form,
    };
  });
}

/** Rate confirmations are the paperwork itself; everything else is a screenshot. */
export function loadSourceFor(format: OcrParseResult["format"]): LoadSource {
  return format === OcrFormat.ratecon ||
    format === OcrFormat.trinity ||
    format === OcrFormat.evans ||
    format === OcrFormat.landstar
    ? LoadSource.ratecon
    : LoadSource.screenshot;
}

export type OcrNext =
  | { kind: "pick"; rows: BoardRow[] }
  | { kind: "review"; form: LoadFormValues; source: LoadSource }
  | { kind: "fuel" }
  | { kind: "none" };

/**
 * What the Check screen does with a parse: boards and multi-load reads go to
 * the picker; a single load goes to review (Accept fills the form); a fuel
 * price list has nothing to put on a load.
 */
export function nextStepForOcr(parsed: OcrParseResult): OcrNext {
  const loads = ocrLoads(parsed);
  if (loads.length > 1 || (parsed.format === OcrFormat.truckstop_board && loads.length > 0)) {
    return { kind: "pick", rows: ocrLoadsToBoardRows(loads) };
  }
  if (loads.length === 1) {
    return { kind: "review", form: ocrLoadToForm(loads[0]!), source: loadSourceFor(parsed.format) };
  }
  if (parsed.format === OcrFormat.fuel_board) return { kind: "fuel" };
  return { kind: "none" };
}
